import * as setup from '@/setup';
import Button from '@/objects/button';
import GameTab from '@/objects/game-tab';
import Header from '@/objects/header';
import * as auth from '@/utils/auth';
import axios from 'axios';
import * as config from '@/config-APP_TARGET';
import * as audio from '@/utils/audio';
import { HEADER_HEIGHT } from '@/constants';

export default class Rankings extends Phaser.Scene {
	constructor() {
		super({ key: 'Rankings' });
		this.games = [];
		this.currentGameId = null;
		this.rankingsData = {};
		this.paginationData = {}; // Salva dati paginazione per ogni gioco
		this.loadingText = null;
		this.errorText = null;
		this.retryButton = null;
		this.gameTabs = [];
		this.rankingsContainer = [];
		this.scrollStartY = null;
		this.scrollThreshold = 50; // Soglia per attivare scroll (px)
		this.positionIndicator = null;
	}

	create() {
		const width = this.sys.game.config.width;
		const height = this.sys.game.config.height;
		const centerX = width / 2;

		// Background
		this.add
			.image(centerX, height / 2, 'background')
			.setOrigin(0.5, 0.5);

		// Header
		this.createHeader(centerX);

		// Setup scroll touch
		this.setupScrollTouch();

		// Carica la lista dei giochi
		this.loadGames();
	}

	setupScrollTouch() {
		const scene = this;
		const width = this.sys.game.config.width;
		const height = this.sys.game.config.height;
		const startY = 230;
		const endY = height - 100;

		// Area scrollabile (dove sono le classifiche)
		this.scrollArea = this.add.rectangle(width / 2, (startY + endY) / 2, width, endY - startY, 0x000000, 0);
		this.scrollArea.setInteractive({ useHandCursor: false });
		this.scrollArea.setOrigin(0.5, 0.5);

		// Event handlers per scroll touch
		this.scrollArea.on('pointerdown', function(pointer) {
			scene.scrollStartY = pointer.y;
		});

		this.scrollArea.on('pointermove', function(pointer) {
			if (scene.scrollStartY !== null && pointer.isDown) {
				const deltaY = pointer.y - scene.scrollStartY;
				// Feedback visivo opzionale durante scroll
				if (Math.abs(deltaY) > 10) {
					// Potremmo aggiungere un indicatore visivo qui
				}
			}
		});

		this.scrollArea.on('pointerup', function(pointer) {
			if (scene.scrollStartY !== null) {
				const deltaY = pointer.y - scene.scrollStartY;
				const gameId = scene.currentGameId;
				const pagination = scene.paginationData[gameId];

				if (pagination) {
					// Scroll giù (deltaY positivo = movimento verso il basso)
					if (deltaY > scene.scrollThreshold && pagination.hasNext) {
						scene.loadRankingsPage(gameId, pagination.currentPage + 1);
					}
					// Scroll su (deltaY negativo = movimento verso l'alto)
					else if (deltaY < -scene.scrollThreshold && pagination.hasPrevious) {
						scene.loadRankingsPage(gameId, pagination.currentPage - 1);
					}
				}

				scene.scrollStartY = null;
			}
		});
	}

	createHeader(centerX) {
		const headerY = HEADER_HEIGHT / 2;

		// Usa il componente Header
		new Header(this, 'CLASSIFICHE');

		// Pulsante back (icona) - separato dal componente Header
		const backArrow = this.add.image(40, headerY, 'back-icon');
		backArrow.setOrigin(0, 0.5);
		backArrow.setInteractive({ useHandCursor: true });

		backArrow.on('pointerup', () => {
			audio.playSound(this, 'button-click');
			this.scene.start('MainMenu');
		});
	}

	loadGames() {
		const scene = this;
		const token = auth.getToken();

		// Mostra loading
		const width = this.sys.game.config.width;
		const height = this.sys.game.config.height;
		const centerX = width / 2;
		this.loadingText = this.add.text(
			centerX,
			height / 2,
			'Caricamento...',
			{
				fontSize: 32,
				fontFamily: 'Arial',
				fill: '#FFFFFF',
				align: 'center'
			}
		).setOrigin(0.5);

		axios.get(config.apiUrl + '/api/games', {
			headers: {
				'Authorization': 'Bearer ' + token
			}
		})
		.then(function(response) {
			// Prendi solo i primi 3 giochi
			scene.games = response.data.slice(0, 3);
			scene.loadingText.destroy();

			if (scene.games.length > 0) {
				// Usa il game_id dal localStorage se presente e valido, altrimenti il primo della lista
				var savedGameId = auth.getGameId();
				var validSavedGame = savedGameId && scene.games.some(function(g) { return g.id === savedGameId; });
				scene.currentGameId = validSavedGame ? savedGameId : scene.games[0].id;
				scene.createTabs();
				scene.loadRankings(scene.currentGameId);
			} else {
				scene.showError('Nessun gioco disponibile');
			}
		})
		.catch(function(error) {
			console.error('Errore caricamento giochi:', error);
			scene.loadingText.destroy();
			scene.showError('Errore nel caricamento dei giochi');
		});
	}

	createTabs() {
		const width = this.sys.game.config.width;
		const tabsY = 155;
		const tabWidth = width / this.games.length;
		const scene = this;

		// Distruggo i tab precedenti se esistono
		if (this.gameTabs && this.gameTabs.length > 0) {
			this.gameTabs.forEach(function(tab) {
				if (tab && tab.destroy) {
					tab.destroy();
				}
			});
		}
		this.gameTabs = [];

		this.games.forEach(function(game, index) {
			const tabX = (index * tabWidth) + (tabWidth / 2);
			const isSelected = scene.currentGameId === game.id;

			// Creo il tab usando il componente GameTab
			const tab = new GameTab(
				scene,
				tabX,
				tabsY,
				tabWidth - 10,
				game.name,
				isSelected
			);

			// Salvo il gameId nel tab per riferimento
			tab.gameId = game.id;

			// Event handler per il click sul tab
			tab.on('pointerdown', function() {
				if (scene.currentGameId !== game.id) {
					scene.currentGameId = game.id;
					scene.updateTabsSelection();
					scene.loadRankings(game.id);
				}
			});

			scene.gameTabs.push(tab);
		});
	}

	/**
	 * Aggiorna lo stato di selezione di tutti i tab.
	 */
	updateTabsSelection() {
		const scene = this;
		if (this.gameTabs) {
			this.gameTabs.forEach(function(tab) {
				tab.setSelected(tab.gameId === scene.currentGameId);
			});
		}
	}

	loadRankings(gameId) {
		// Carica prima pagina centrata sull'utente
		this.loadRankingsPage(gameId, null);
	}

	loadRankingsPage(gameId, page) {
		const scene = this;
		const token = auth.getToken();
		const currentUser = auth.getUser();
		const userId = currentUser ? currentUser.id : null;

		// Mostra loading
		if (this.rankingsContainer && this.rankingsContainer.length > 0) {
			this.rankingsContainer.forEach(function(child) {
				if (child && child.destroy) {
					child.destroy();
				}
			});
			this.rankingsContainer = [];
		}

		if (this.positionIndicator) {
			this.positionIndicator.destroy();
			this.positionIndicator = null;
		}

		if (this.errorText) {
			this.errorText.destroy();
			this.errorText = null;
		}

		if (this.retryButton) {
			this.retryButton.destroy();
			this.retryButton = null;
		}

		const width = this.sys.game.config.width;
		const height = this.sys.game.config.height;
		const centerX = width / 2;
		this.loadingText = this.add.text(
			centerX,
			height / 2,
			'Caricamento classifica...',
			{
				fontSize: 28,
				fontFamily: 'Arial',
				fill: '#FFFFFF',
				align: 'center'
			}
		).setOrigin(0.5);

		// Costruisci URL con parametri
		let url = config.apiUrl + '/api/ranking?game_id=' + gameId + '&per-page=11';
		if (userId && page === null) {
			// Prima chiamata: centra sull'utente
			url += '&user_id=' + userId;
		} else if (page !== null) {
			// Paginazione normale
			url += '&page=' + page;
		}

		axios.get(url, {
			headers: {
				'Authorization': 'Bearer ' + token
			}
		})
		.then(function(response) {
			scene.loadingText.destroy();

			// Leggi header paginazione
			const paginationHeaders = {
				currentPage: parseInt(response.headers['x-pagination-current-page'] || '1', 10),
				pageCount: parseInt(response.headers['x-pagination-page-count'] || '1', 10),
				perPage: parseInt(response.headers['x-pagination-per-page'] || '11', 10),
				totalCount: parseInt(response.headers['x-pagination-total-count'] || '0', 10)
			};

			paginationHeaders.hasPrevious = paginationHeaders.currentPage > 1;
			paginationHeaders.hasNext = paginationHeaders.currentPage < paginationHeaders.pageCount;

			// Salva dati paginazione
			scene.paginationData[gameId] = paginationHeaders;

			// Gestisci risposta (array diretto)
			const rankings = Array.isArray(response.data) ? response.data : [];

			if (rankings.length > 0) {
				scene.rankingsData[gameId] = rankings;
				scene.displayRankings(gameId);
			} else {
				scene.showError('Nessun dato disponibile');
			}
		})
		.catch(function(error) {
			console.error('Errore caricamento classifica:', error);
			scene.loadingText.destroy();
			scene.showError('Errore nel caricamento della classifica');
		});
	}

	displayRankings(gameId) {
		const width = this.sys.game.config.width;
		const height = this.sys.game.config.height;
		const startY = 220;
		const rowHeight = 70;
		const scene = this;

		// Distruggi elementi precedenti se esistono
		if (this.rankingsContainer) {
			this.rankingsContainer.forEach(function(child) {
				if (child && child.destroy) {
					child.destroy();
				}
			});
			this.rankingsContainer = [];
		} else {
			this.rankingsContainer = [];
		}

		const rankings = this.rankingsData[gameId] || [];
		const currentUserId = auth.getUser() ? auth.getUser().id : null;


		// Intestazione colonne
		const headerY = startY;
		const headerBg = scene.add.rectangle(width / 2, headerY, width - 40, 40, 0x2a7a9a, 0.8);
		scene.rankingsContainer.push(headerBg);

		const posHeader = scene.add.text(60, headerY, 'POS', {
			fontSize: 40,
			fontFamily: 'Luckiest Guy',
			fill: '#FFD700',
			align: 'left'
		}).setOrigin(0, 0.5);
		scene.rankingsContainer.push(posHeader);

		const nicknameHeader = scene.add.text(180, headerY, 'NICKNAME', {
			fontSize: 40,
			fontFamily: 'Luckiest Guy',
			fill: '#FFD700',
			align: 'left'
		}).setOrigin(0, 0.5);
		scene.rankingsContainer.push(nicknameHeader);

		const puntiHeader = scene.add.text(width - 60, headerY, 'PUNTEGGIO', {
			fontSize: 40,
			fontFamily: 'Luckiest Guy',
			fill: '#FFD700',
			align: 'right'
		}).setOrigin(1, 0.5);
		scene.rankingsContainer.push(puntiHeader);

		// Indicatore posizione
		const pagination = this.paginationData[gameId];
		if (pagination) {
			const firstPosition = (pagination.currentPage - 1) * pagination.perPage + 1;
			const lastPosition = Math.min(pagination.currentPage * pagination.perPage, pagination.totalCount);
			const positionText = 'Posizione ' + firstPosition + '-' + lastPosition + ' di ' + pagination.totalCount;

			this.positionIndicator = scene.add.text(
				width / 2,
				height - 60,
				positionText,
				{
					fontSize: 20,
					fontFamily: 'Arial',
					fill: '#FFFFFF',
					align: 'center'
				}
			).setOrigin(0.5);
		}

		// Lista classifiche
		if (rankings.length === 0) {
			const noDataText = scene.add.text(
				width / 2,
				startY + 100,
				'Nessun dato disponibile',
				{
					fontSize: 28,
					fontFamily: 'Arial',
					fill: '#CCCCCC',
					align: 'center'
				}
			).setOrigin(0.5);
			scene.rankingsContainer.push(noDataText);
			return;
		}

		rankings.forEach(function(ranking, index) {
			const y = startY + 60 + (index * rowHeight);

			// Evita di andare oltre l'altezza dello schermo
			if (y > height - 100) {
				return;
			}

			// Gestisci sia la struttura custom (con position e user) che quella del modello (con user_id)
			const position = ranking.position || (index + 1);
			let userId = null;
			let nickname = 'User';

			if (ranking.user) {
				// Struttura custom con user object
				userId = ranking.user.id;
				nickname = ranking.user.nickname || ('User ' + ranking.user.id);
			} else if (ranking.user_id) {
				// Struttura del modello con user_id
				userId = ranking.user_id;
				nickname = 'User ' + ranking.user_id;
			}

			const isCurrentUser = currentUserId && userId === currentUserId;
			const rowColor = isCurrentUser ? 0x2a7a9a : (index % 2 === 0 ? 0x1a5a7a : 0x1567a3);
			const rowAlpha = isCurrentUser ? 0.9 : 0.6;

			// Background riga
			const rowBg = scene.add.rectangle(width / 2, y, width - 40, rowHeight - 5, rowColor, rowAlpha);
			if (isCurrentUser) {
				rowBg.setStrokeStyle(2, 0xffd700);
			}

			// Posizione
			const positionText = scene.add.text(60, y, String(position), {
				fontSize: 48,
				fontFamily: 'Luckiest Guy',
				fill: isCurrentUser ? '#FFD700' : '#FFFFFF',
				align: 'left'
			}).setOrigin(0, 0.5);
			const nicknameText = scene.add.text(180, y, nickname, {
				fontSize: 44,
				fontFamily: 'Arial',
				fill: isCurrentUser ? '#FFD700' : '#FFFFFF',
				align: 'left'
			}).setOrigin(0, 0.5);

			// Punteggio combinato
			const combinedScore = ranking.combined_score !== undefined ? ranking.combined_score :
				(ranking.total_score ? ranking.total_score : 0);
			const scoreText = scene.add.text(width - 60, y, scene.formatLargeNumber(combinedScore), {
				fontSize: 44,
				fontFamily: 'Arial',
				fill: isCurrentUser ? '#FFD700' : '#FFFFFF',
				align: 'right'
			}).setOrigin(1, 0.5);

			scene.rankingsContainer.push(rowBg, positionText, nicknameText, scoreText);
		});
	}

	/**
	 * Formatta numeri grandi con suffissi k (migliaia) e M (milioni)
	 * @param {number} score - Il punteggio da formattare
	 * @returns {string} - Stringa formattata (es. "999", "1.5k", "2.3M")
	 */
	formatLargeNumber(score) {
		if (!score && score !== 0) {
			return '0';
		}

		const num = parseInt(score, 10);

		if (num < 1000) {
			return String(num);
		} else if (num < 1000000) {
			const kValue = num / 1000;
			// Arrotonda a 1 decimale se necessario
			if (kValue % 1 === 0) {
				return String(kValue) + 'k';
			} else {
				return kValue.toFixed(1) + 'k';
			}
		} else {
			const mValue = num / 1000000;
			// Arrotonda a 1 decimale se necessario
			if (mValue % 1 === 0) {
				return String(mValue) + 'M';
			} else {
				return mValue.toFixed(1) + 'M';
			}
		}
	}

	showError(message) {
		const width = this.sys.game.config.width;
		const height = this.sys.game.config.height;
		const centerX = width / 2;

		if (this.errorText) {
			this.errorText.destroy();
		}

		if (this.retryButton) {
			this.retryButton.destroy();
		}

		this.errorText = this.add.text(centerX, height / 2, message, {
			fontSize: 28,
			fontFamily: 'Arial',
			fill: '#FF6666',
			align: 'center',
			wordWrap: { width: width - 100 }
		}).setOrigin(0.5);

		// Bottone riprova
		this.retryButton = new Button(
			this,
			centerX,
			height / 2 + 80,
			'green-button',
			1.5,
			'Riprova',
			false
		);

		const scene = this;
		this.retryButton.on('pointerup', function() {
			scene.errorText.destroy();
			scene.errorText = null;
			scene.retryButton.destroy();
			scene.retryButton = null;
			scene.loadGames();
		});
	}
}
